import { inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ProcessedFile } from "@dtm-frontend/shared/ui";
import { RxjsUtils } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { DataSource, KmlDataSource } from "cesium";
import { BehaviorSubject } from "rxjs";
import { CesiumMapService } from "../../../services/cesium-map.service";
import { KmlImportModalComponent } from "./kml-import-modal/kml-import-modal.component";

export interface KmlData {
    dataSource: KmlDataSource;
    fileInfo: ProcessedFile;
    data: DataSource;
}

const ZOOM_ANIMATION_DURATION = 0.5;

@UntilDestroy()
@Injectable()
export class KmlDataControlService {
    private readonly kmlDataSetSubject = new BehaviorSubject<KmlData[]>([]);
    public readonly kmlDataSet$ = this.kmlDataSetSubject.asObservable();
    private cesiumViewer = inject(CesiumMapService).getViewer();

    public get kmlFiles(): ProcessedFile[] {
        return this.kmlDataSetSubject.value.map((kmlData) => kmlData.fileInfo);
    }

    constructor(private readonly dialog: MatDialog) {}

    public setKmlData(files: ProcessedFile[]): void {
        const kmlDataSet = this.kmlDataSetSubject.value;

        kmlDataSet.forEach((kmlData) => {
            if (files.some((file) => kmlData.fileInfo.id === file.id)) {
                return;
            }

            this.cesiumViewer.dataSources.remove(kmlData.dataSource);

            this.kmlDataSetSubject.next(
                this.kmlDataSetSubject.value.filter((kmlDataItem) => kmlDataItem.fileInfo.id !== kmlData.fileInfo.id)
            );
        });

        if (!files.length) {
            this.kmlDataSetSubject.next([]);
        }

        files?.forEach((file) => {
            if (kmlDataSet.some((kmlData) => kmlData.fileInfo.id === file.id)) {
                return;
            }

            const kmlDataSource = new KmlDataSource();
            kmlDataSource.load(file.file, { clampToGround: true });

            this.cesiumViewer.dataSources.add(kmlDataSource).then((data) => {
                this.kmlDataSetSubject.next([
                    ...this.kmlDataSetSubject.value,
                    {
                        dataSource: kmlDataSource,
                        fileInfo: file,
                        data,
                    },
                ]);
            });
        });

        this.refreshView();
    }

    public refreshView() {
        this.cesiumViewer.scene.requestRender();
    }

    public zoomToKmlData(kmlData: KmlData) {
        this.cesiumViewer.flyTo(kmlData.data, { duration: ZOOM_ANIMATION_DURATION });
    }

    public deleteAllKmlData() {
        this.kmlDataSetSubject.value.forEach((kmlData) => {
            this.cesiumViewer.dataSources.remove(kmlData.dataSource);
        });
        this.kmlDataSetSubject.next([]);
        this.refreshView();
    }

    public openKmlImportModal() {
        this.dialog
            .open(KmlImportModalComponent, { data: this.kmlFiles })
            .afterClosed()
            .pipe(RxjsUtils.filterFalsy(), untilDestroyed(this))
            .subscribe((files) => {
                this.setKmlData(files);
            });
    }
}
